import Big from 'big.js'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { AnyToken } from 'src/common/types/models/Tokens'
import Icon from 'src/common/ui-kit/Icon/Icon'
import IconButton from 'src/common/ui-kit/IconButton'
import { safeDiv } from 'src/common/utils/big-utils'
import useSyncTradeSearchParams from '../hooks/useSyncTradeSearchParams'
import useTradePageState from '../hooks/useTradePageState'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  limitOrderOptionsAtom,
  pricePerUnitAtom,
  priceSwappedAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
} from '../stores/trade-store'
import TokenWithAmountSelector from '../../../common/components/TokenWithAmountSelector/TokenWithAmountSelector'

export interface TokenPairSelectProps {
  titleBuy?: string
  titleSell?: string
  readonlyBuy?: boolean
  excludeNFT?: boolean
  hideSwap?: boolean
  onBuyTokenChange?: (value: AnyToken) => void
  onSellTokenChange?: (value: AnyToken) => void
  onBuyTokenAmountChange?: (value: Big) => void
  onSellTokenAmountChange?: (value: Big) => void
  onSwap?: () => void
}

const TokenPairSelect: React.FC<TokenPairSelectProps> = ({
  titleBuy,
  titleSell,
  readonlyBuy,
  excludeNFT,
  hideSwap,
  onBuyTokenChange,
  onBuyTokenAmountChange,
  onSellTokenChange,
  onSellTokenAmountChange,
  onSwap,
}) => {
  useSyncTradeSearchParams()

  const { isLimitMode, isSwapMode } = useTradePageState()

  const [sellToken, setSellToken] = useAtom(sellTokenAtom)
  const [sellTokenAmount, setSellTokenAmount] = useAtom(sellTokenAmountAtom)
  const [buyToken, setBuyToken] = useAtom(buyTokenAtom)
  const [buyTokenAmount, setBuyTokenAmount] = useAtom(buyTokenAmountAtom)

  const setPricePerUnit = useSetAtom(pricePerUnitAtom)
  const priceSwapped = useAtomValue(priceSwappedAtom)

  const limitOfferOptions = useAtomValue(limitOrderOptionsAtom)
  const buyAmountDisabled = isLimitMode && limitOfferOptions.dynamic

  const handleSwapClick = () => {
    setSellToken(buyToken)
    setSellTokenAmount(buyTokenAmount)
    setBuyToken(sellToken)
    setBuyTokenAmount(sellTokenAmount)
    onSwap?.()

    const pricePerUnitValue = priceSwapped
      ? safeDiv(buyTokenAmount, sellTokenAmount)
      : safeDiv(sellTokenAmount, buyTokenAmount)

    setPricePerUnit(pricePerUnitValue)
  }

  const handleSellTokenChange = (token: AnyToken) => {
    setSellToken(token)
    onSellTokenChange?.(token)
  }

  const handleBuyTokenChange = (token: AnyToken) => {
    setBuyToken(token)
    onBuyTokenChange?.(token)
  }

  const updateSellAmount = (value: Big) => {
    setSellTokenAmount(value)
    onSellTokenAmountChange?.(value)
  }

  const updateBuyAmount = (value: Big) => {
    setBuyTokenAmount(value)
    onBuyTokenAmountChange?.(value)
  }

  return (
    <div>
      <TokenWithAmountSelector
        title={titleSell ?? 'From'}
        token={sellToken}
        amount={sellTokenAmount}
        onTokenChange={handleSellTokenChange}
        onTokenAmountChange={updateSellAmount}
        amountControl
        excludeToken={buyToken}
        className={!hideSwap ? '' : 'mb-2'}
        excludeNFT={excludeNFT}
      />
      {!hideSwap && (
        <IconButton
          hoverBackground='paper-hover'
          className='w-7 h-7 flex justify-center items-center bg-paper rounded-md relative border border-secondary-accent cursor-pointer'
          style={{
            margin: '-0.6rem auto',
          }}
          onClick={handleSwapClick}
        >
          <Icon name='swap' />
        </IconButton>
      )}
      <TokenWithAmountSelector
        title={titleBuy ?? 'To'}
        token={buyToken}
        disabled={buyAmountDisabled}
        amount={buyTokenAmount}
        onTokenChange={handleBuyTokenChange}
        onTokenAmountChange={updateBuyAmount}
        excludeToken={sellToken}
        enableNFTBatching={isSwapMode}
        viewBalance={buyToken?.type === 'ERC20'}
        nftGroup
        readonly={readonlyBuy}
        excludeNFT={excludeNFT}
      />
    </div>
  )
}

export default TokenPairSelect
